/**
 * Gemini access for LocalDiff.dev.
 *
 * Every call goes through the same-origin /api-proxy endpoint (server/server.js),
 * which holds the API key. In local development Vite proxies /api-proxy to the
 * Express server on :3001, so no key is ever compiled into the client bundle and
 * the Gemini SDK is not shipped to browsers.
 */

async function callGemini(model: string, contents: string): Promise<string> {
  const response = await fetch('/api-proxy', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ model, contents }),
  });
  if (!response.ok) {
    throw new Error(`API proxy returned ${response.status}`);
  }
  const data = await response.json();
  return data?.candidates?.[0]?.content?.parts?.[0]?.text || '';
}

export const summarizeDifferences = async (textA: string, textB: string): Promise<string> => {
  try {
    return await callGemini(
      'gemini-2.0-flash',
      `You are a world-class code reviewer. Summarize the key differences between these two versions of text concisely and professionally.\n\nOriginal Version:\n${textA}\n\nModified Version:\n${textB}`
    ) || 'No summary available.';
  } catch (error) {
    console.error('Gemini Summarization Error:', error);
    return 'AI Summarization is currently unavailable.';
  }
};

export const smartMergeConflict = async (chunkA: string, chunkB: string, context: string): Promise<string> => {
  try {
    const result = await callGemini(
      'gemini-2.0-flash',
      `I have a merge conflict between two code snippets. Based on the surrounding context, provide the most logical merged version.\n\nContext:\n${context}\n\nSnippet A (Original):\n${chunkA}\n\nSnippet B (Modified):\n${chunkB}\n\nProvide ONLY the merged code without any markdown formatting or explanations.`
    );
    return result?.trim() || chunkB;
  } catch (error) {
    console.error('Gemini Smart Merge Error:', error);
    return chunkB;
  }
};

export const explainChange = async (text: string, type: 'added' | 'removed'): Promise<string> => {
  try {
    return await callGemini(
      'gemini-2.0-flash',
      `Briefly explain the intent or purpose of this code block which was ${type}:\n\n${text}`
    ) || 'Explanation unavailable.';
  } catch (error) {
    return 'AI Explanation failed.';
  }
};

export const refineFeedback = async (message: string, type: string): Promise<string> => {
  try {
    return await callGemini(
      'gemini-2.0-flash',
      `Rewrite this user feedback to be more professional and clear for a development team. The feedback is about a ${type}.\n\nUser Message: ${message}`
    ) || message;
  } catch (error) {
    return message;
  }
};
